import React, { useMemo, useCallback, useState, useRef, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";

import ERRORS from "common/errors";
import stringEncryptRSA from "common/string-encrypt-rsa";
import api from "services/api";
import pathnames from "routes/pathnames";
import serveRequestErrors from "common/serve-request-errors";
import AppAlert from "components/app-alert";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import logo from "assets/images/logo.webp";
import hero from "assets/images/components/pages/create-password/create-password.jpg";
import activateIcon from "assets/images/activate-icon.svg";
import exclamationIcon from "assets/images/exclamation-icon.svg";
import AppOtpModal from "components/pages/signup/app-otp-modal";

const PageCreatePassword = (props) => {
    const [searchParams] = useSearchParams();
    const [token, setToken] = useState("");
    const [email, setEmail] = useState("");
    const otpModalRef = useRef();
    const navigate = useNavigate();
    const formik = useFormik({
        initialValues: {
            password: "",
            confirmPassword: "",
        },
        validationSchema: yup.object({
            password: yup.string().required(ERRORS.REQUIRED).test("no-space", ERRORS.SPACE, (value) => !/\s/.test(value || "")),
            confirmPassword: yup.string().required(ERRORS.REQUIRED).test("no-space", ERRORS.SPACE, (value) => !/\s/.test(value || "")),
        }),
        onSubmit: (values) => {
            onHandleSendOtp();
        },
    });

    const rules = useMemo(() => [
        {
          label: "Use at least 8 characters",
          isValid: formik.values.password.length >= 8,
        },
        {
          label: "Should contain at least 1 uppercase",
          isValid: /[A-Z]/.test(formik.values.password),
        },
        {
          label: "Should contain at least 1 numeric",
          isValid: /[0-9]/.test(formik.values.password),
        },
        {
          label: "Should contain at least 1 special character",
          isValid: /[!@#$%^&*,.?]/.test(formik.values.password),
        },
      ], [formik.values.password]);

    const isFormValid = useMemo(() => {
        const allRulesPassed = rules.every(rule => rule.isValid);
        return allRulesPassed && formik.values.password === formik.values.confirmPassword;
      }, [rules, formik.values.password, formik.values.confirmPassword]);

    const onHandleSignUp = useCallback(async (otpCode) => {
        let values = { ...formik.values, otpCode };

        try {
            const payload = {
                email: email,
                password: stringEncryptRSA(values.password),
                otpCode: values.otpCode,
                token: token,
            };
            console.log(payload);

            await api.post.authenticate.createPassword(payload);
            navigate(pathnames.login);

        } catch (error) {
            serveRequestErrors(error, (message) => {
                formik.setFieldError("confirmPassword", message);
            });
        } finally {
            formik.setSubmitting(false);
        }
    }, [formik, navigate, token]);

    useEffect(() => {
        const tokenFromUrl = searchParams.get("token");
        const emailFromUrl = searchParams.get("email");

        if (tokenFromUrl) {
            setToken(tokenFromUrl);
        }
        if (emailFromUrl) {
            setEmail(emailFromUrl);
        }
    }, [searchParams]);

    const onHandleOtpModal = useCallback(() => {
        otpModalRef.current.onHandleShow();
    }, []);

    const onHandleSendOtp = async () => {
		try {
			await api.post.authenticate.sendOtp({ 
                email: email, 
                token: token,
            });

			formik.setSubmitting(false);
			onHandleOtpModal();

		} catch (error) {
			serveRequestErrors(error, (message) => {
				formik.setFieldError("confirmPassword", message);
			});
		} finally {
			formik.setSubmitting(false);
		}
	}

    return (
        <div className="page-create-password">
            <div className="create-password">
                <AppAlert />

                <main className="main">
                    <div className="main__content main__content--background">
                        <Link to={pathnames.login} className="main__logo">
                            <img src={logo} alt="logo" />
                        </Link>
                    </div>

                    <div className="main__content main__content--form">
                        <form className="main__form" onSubmit={formik.handleSubmit}>
                            {/* <img className="main__hero" src={hero} alt="hero" /> */}
                            <h1 className="main__title">Create your password</h1>

                            {/* prettier-ignore */}
                            <AppInput type="password" name="password" label="Enter Password" placeholder="Password" value={formik.values.password} error={formik.errors.password} touched={formik.touched.password} onChange={formik.handleChange} />

                            <ul className="main__rules">
                                {rules.map((rule, idx) => (
                                    <li key={idx} className="main__rules__maps">
                                        {rule.isValid ? (
                                            <img src={activateIcon} alt="activateIcon" className="main__rules__maps__icon" />
                                        ) : (
                                            <img src={exclamationIcon} alt="exclamationIcon" className="main__rules__maps__icon" />
                                        )}
                                        <span className={rule.isValid ? "main__rules__maps__text-green" : "main__rules__maps__text-gray"}>
                                            {rule.label}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <AppInput type="password" name="confirmPassword" label="Confirm New Password" placeholder="Confirm Password" value={formik.values.confirmPassword} error={formik.errors.confirmPassword} touched={formik.touched.confirmPassword} onChange={formik.handleChange} />

                            <div className="main__button-container">
                                <AppButton type="submit" label="Create Password" disabled={!isFormValid} />
                            </div>
                        </form>

                        <p className="main__copyright">© {new Date().getFullYear()} Altecflex.my. All Rights Reserved.</p>
                    </div>
                </main>
            </div>
            <AppOtpModal token={token} email={email} ref={otpModalRef} onSubmit={onHandleSignUp} />
        </div>
    );
};

export default PageCreatePassword;
